import { useEffect } from "react";

type UseImagePreloadArgs = {
  /** Only inject the hint when this is `true` (e.g. priority/LCP images). */
  enabled: boolean;
  /** Must match the rendered `<img srcset>` so the browser reuses the fetch. */
  srcSet?: string;
  /** Must match the rendered `<img sizes>` so the right candidate is chosen. */
  sizes?: string;
  /** Single-URL fallback when no srcset is available. */
  href?: string;
};

/**
 * Injects a `<link rel="preload" as="image">` for a high-priority image so the
 * download starts during head parsing — before React even mounts the `<img>` —
 * which is the single biggest lever on LCP for a hero/banner.
 *
 * `imagesrcset`/`imagesizes` mirror the img's `srcset`/`sizes` exactly, so the
 * browser preloads the very candidate it will later paint and dedupes the two
 * requests into one. The hint is removed on unmount / dependency change.
 */
export function useImagePreload({ enabled, srcSet, sizes, href }: UseImagePreloadArgs): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof document === "undefined") return;
    if (!srcSet && !href) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.setAttribute("fetchpriority", "high");
    if (srcSet) link.setAttribute("imagesrcset", srcSet);
    if (sizes) link.setAttribute("imagesizes", sizes);
    if (href) link.href = href;

    document.head.appendChild(link);

    return () => {
      link.remove();
    };
  }, [enabled, srcSet, sizes, href]);
}
